import { getGridSingleSelectOperators } from "@mui/x-data-grid-pro";
import {
  documentFileFieldConverter,
  DocumentFileFieldView,
} from "./modelComponents/viewFields/documentFileFieldView";
import DocumentFileField from "./modelComponents/formFields/DocumentFileField";
import { CaptableFundFieldView } from "./modelComponents/viewFields/captableFundFieldView";
import { width } from "@mui/system";
import CaptableUpdateModal from "./pages/modals/CaptableUpdateModal";

const modelExtraData = {
  "backoffice.Deal": {
    fields: {
      id: {
        width: 100,
        hidden: true,
      },
      name: {
        width: 100,
        pinned: "left",
        link: true,
      },
      date: {
        sorted: "desc",
      },
      logo: {
        type: "logo",
      },
      owner: {
        filterOperators: [
          {
            value: "is me",
            label: "is me",
          },
          ...getGridSingleSelectOperators(),
        ],
      },
      funds: {
        filterOperators: [
          {
            value: "is mine",
            label: "is mine",
          },
          ...getGridSingleSelectOperators(),
        ],
      },
    },
    hiddenFields: ["slug", "deal_reporting_id"],
    disabledFields: ["slug", "id"],
    extraActions: [
      {
        name: "import-data",
        hookName: "useImportData",
        label: "Import Data",
        type: "mutation",
        url: ({ dealId }) => `${dealId}/import-data/`,
        extraCacheKeys: ({ dealId }) => {
          return [["visualization.DealVisualizationInformation", dealId]];
        },
      },
      {
        name: "create-excel-year",
        hookName: "useCreateExcelYear",
        label: "Create Excel Year",
        type: "mutation",
        url: ({ dealId, year, withOldData }) =>
          `${dealId}/create-excel-year/?year=${year}&withOldData=${withOldData}`,
      },
      {
        name: "validate-all",
        hookName: "useValidateAll",
        label: "Validate All",
        type: "mutation",
        url: ({ dealId }) => `${dealId}/validate-all/`,
        extraCacheKeys: ({ dealId }) => {
          return [["visualization.DealVisualizationInformation", dealId]];
        },
      },
    ],
    extraCacheKeys: (data) => {
      return [
        ["visualization.DealVisualizationInformation"],
        ["visualization.FundVisualizationInformation"],
      ];
    },
  },
  "backoffice.Fund": {
    fields: {
      id: {
        hidden: true,
      },
      name: {
        width: 100,
        pinned: "left",
        link: true,
      },
      full_name: {},
      logo: {
        type: "logo",
      },
      slug: {
        hidden: true,
      },
    },
    extraCacheKeys: (data) => {
      return [
        ["visualization.DealVisualizationInformation"],
        ["visualization.FundVisualizationInformation"],
      ];
    },
  },
  "backoffice.DealFund": {
    fields: {
      id: {
        hidden: true,
      },
      fund: {
        width: 180,
        align: "left",
      },
      deal_status_summary: {
        width: 170,
        align: "left",
      },
      instrument: {
        width: 150,
        align: "left",
      },
    },
    extraCacheKeys: (data) => {
      return [["backoffice.Deal", data.deal]];
    },
  },
  "backoffice.Contact": {
    fields: {
      id: {
        hidden: true,
      },
    },
  },
  "operations.Operation": {
    fields: {
      id: {
        hidden: true,
      },
      name: {
        width: 100,
        pinned: "left",
        link: true,
      },
    },
    fields_order: ["id", "name", "deal"],
  },
  "reporting.DealInformation": {
    fields: {
      id: {
        hidden: true,
      },
    },
    fields_order: ["deal"],
    extraCacheKeys: (data) => {
      return [["visualization.DealVisualizationInformation", data.deal]];
    },
  },
  "reporting.DealReporting": {
    fields: {
      id: {
        hidden: true,
      },
      deal_id: {
        hidden: true,
      },
    },
    fields_order: ["deal"],
    extraCacheKeys: (data) => {
      return [["visualization.DealVisualizationInformation", data.deal]];
    },
  },
  "reporting.DealExit": {
    fields: {
      id: {
        hidden: true,
      },
      deal_id: {
        hidden: true,
      },
    },
    fields_order: ["deal"],
    extraCacheKeys: (data) => {
      return [["visualization.DealVisualizationInformation", data.deal]];
    },
  },
  "reporting.CaptableLight": {
    fields: {
      captable_id: {
        hidden: true,
      },
      reference_date: {
        pinned: "left",
        link: true,
      },
    },
    fields_order: ["deal", "reference_date", "captable"],
    updateModal: CaptableUpdateModal("reporting.CaptableLight"),
    extraCacheKeys: ({ deal }) => {
      return [["visualization.DealVisualizationInformation", deal]];
    },
  },
  "backoffice.DocumentFile": {
    fields: {
      id: {
        hidden: true,
      },
    },
    viewField: DocumentFileFieldView,
    formComponent: DocumentFileField,
    converter: documentFileFieldConverter,
  },
  "reporting.BoardMeeting": {
    fields: {
      id: {
        hidden: true,
      },
      date: {
        type: "dateField",
        pinned: "left",
        link: true,
      },
    },
    fields_order: ["deal", "tag", "date"],
  },
  "reporting.NutsCode": {},
  "reporting.NaceCode": {},
  "reporting.Country": {},
  "hr.GoogleUser": {
    fields: {
      primary_email: {
        pinned: "left",
      },
      parents: {
        type: "manyRelatedField",
        subModel: "hr.GoogleGroup",
        label: "Parents",
      },
    },
  },
  "hr.GoogleGroup": {
    fields: {
      name: {
        pinned: "left",
      },
      parents: {
        type: "manyRelatedField",
        subModel: "hr.GoogleGroup",
        label: "Parents",
      },
    },
  },
  "financials.FinancialDataType": {
    fields: {
      name: {
        width: 100,
        pinned: "left",
        link: true,
      },
    },
    fields_order: ["deal", "name"],
  },
  "reporting.CaptableFund": {
    fields: {
      id: {
        hidden: true,
      },
      fund_id: {
        label: "Fund",
      },
      captable: {
        hidden: true,
      },
    },
    fields_order: ["fund"],
  },
  "reporting.CaptableOption": {
    fields: {
      id: {
        hidden: true,
      },
      captable: {
        hidden: true,
      },
    },
  },
  "visualization.DealVisualizationInformation": {
    extraActions: [
      {
        name: "get-by-ids",
        hookName: "useGetByIds", //=> useGetByIds in the customDataSource
        label: "Get by ids",
        type: "query",
        url: ({ dealId, fundId, valuationPeriodId }) =>
          `get-by-ids/?dealId=${dealId}&fundId=${fundId}&valuationPeriodId=${valuationPeriodId}`,
      },
      {
        name: "get-all-by-ids",
        hookName: "useGetAllByIds", //=> useGetByIds in the customDataSource
        label: "Get all by ids",
        type: "query",
        url: ({ fundId, valuationPeriodId }) =>
          `get-all-by-ids/?fundId=${fundId}&valuationPeriodId=${valuationPeriodId}`,
      },
      {
        name: "get-by-deal-id",
        hookName: "useGetByDealId", //=> useGetByIds in the customDataSource
        label: "Get by deal id",
        type: "query",
        url: ({ dealId }) => `get-by-deal-id/?dealId=${dealId}`,
      },
    ],
  },
  "visualization.FundVisualizationInformation": {
    extraActions: [
      {
        name: "get-by-ids",
        hookName: "useGetByIds", //=> useGetByIds in the customDataSource
        label: "Get by ids",
        type: "query",
        url: ({ fundId, valuationPeriodId }) =>
          `get-by-ids/?fundId=${fundId}&valuationPeriodId=${valuationPeriodId}`,
      },
    ],
  },
  "visualization.ScreenshotsRecord": {
    extraActions: [
      {
        name: "get-last-by-name",
        hookName: "useGetLastByName", //=> useGetLastByName in the customDataSource
        label: "Get last by name",
        type: "query",
        url: ({ name }) => `get-last-by-name/?name=${name}`,
      },
    ],
  },
};

export default modelExtraData;
